import { FunctionComponent, useEffect, useState } from "react"

// Redux
import { connect } from "react-redux"
import { AppState } from "src/store"

// Services and utilities
import approvalService from "../../ApprovalService"
import snackBar from "src/utilities/snackBar/SnackBar"

// Interfaces and constants
import { I_CreateApprovalWorkflow } from "../../interfaces"

// UI components
import Select from "react-select"
import { Button } from "react-bootstrap"

// Styles
import "./CreateApprovalWF.css"

interface Props {
  recordTypeId: number
  recordId: number
  employeeId: number
  initiatedBy: number
  allowedReviewers: { name: string; id: number }[]
  templateGroupId: number
  finishedCallback?: VoidFunction
  cancelCallback?: VoidFunction
  closeBtnText?: string
  setLoading?: VoidFunction
}

/**
 * CreateApprovalWF component
 * Allows user to select reviewers and create a new approval workflow
 */
const CreateApprovalWF: FunctionComponent<Props> = ({
  recordTypeId,
  recordId,
  employeeId,
  initiatedBy,
  allowedReviewers,
  templateGroupId,
  finishedCallback,
  cancelCallback,
  closeBtnText,
  setLoading
}) => {

  const [reviewers, setReviewers] = useState<{ label: string; value: number }[]>([])

  const { initialized, init, creatNewWF } = approvalService

  const payload: I_CreateApprovalWorkflow = {
    RecordTypeId: recordTypeId,
    RecordId: recordId,
    EmployeeId: employeeId,
    InitiatedBy: initiatedBy,
    StatusTypeId: 1,
    ApproverUserIds: reviewers.map(r => r.value),
    TemplateGroupId: templateGroupId
  }

  useEffect(() => {
    const initialize = async () => {
      if (!initialized) {
        await init(recordTypeId)
      }
    }

    initialize()
  }, [init, initialized, recordTypeId])

  /**
   * Submits a new approval workflow using the current form state
   */
  const createNewWorkflow = async () => {
    if (setLoading) {
      setLoading()
    }

    await creatNewWF(payload)

    if (finishedCallback) {
      finishedCallback()
    }

    snackBar({ type: "primary", text: "Approval workflow created", timeout: 4000 })
  }

  return (
    <div className="CreateApprovalWF">
      {/* Reviewer selection dropdown */}
      <Select<{ label: string, value: number }>
        isMulti
        options={allowedReviewers
          .filter(r => r.id !== initiatedBy)
          .map(r => ({ label: r.name, value: r.id }))}
        value={reviewers}
        onChange={(selected: (readonly { label: string; value: number }[]) | null) => {
          setReviewers(selected ? [...selected] : [])
        }}
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base: React.CSSProperties) => ({ ...base, zIndex: 9999 }),
        }}
        isClearable
        placeholder="Select reviewers..."
      />

      {/* Footer with Cancel / Create buttons */}
      <div className="CreateApprovalWFFooter">
        <div className="CreateApprovalWFFooterLeft">
          {cancelCallback && (
            <Button variant="secondary" onClick={cancelCallback}>
              {`${closeBtnText ? closeBtnText : "Cancel"}`}
            </Button>
          )}
        </div>

        <div className="CreateApprovalWFFooterRight">
          <Button onClick={createNewWorkflow}>Create</Button>
        </div>
      </div>
    </div>
  )
}

// Map Redux state to props
const mapStateToProps = (state: AppState) => ({
  initiatedBy: parseInt(state.user.userId),
})

export default connect(mapStateToProps)(CreateApprovalWF)
